import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Typography, StandardButton, Stack } from '@moonsifttech/design-system';

import TopMenu from 'src/mvp22/menu-components/TopMenu';
import { NotProvisionedProps } from './NotProvisioned.types';

const UnstyledNotProvisioned: React.FC<NotProvisionedProps> = ({
  className,
  style,
  isProvisioning,
  provisioningFailed,
  justLoggedIn,
  handleProvisioning,
  handleForceReload,
  handleGoBack,
}) => {
  return (
    <>
      <div className={className} style={style}>
        <TopMenu className="NotProvisioned-topMenu" />
        <div className="NotProvisioned-content">
          {
            /* Case that for some reason the user data does not load even if the account is provisioned, but the user data fails to load */
            !justLoggedIn && (
              <Stack>
                <Helmet>
                  <title>You appear to be offline - Moonsift</title>
                </Helmet>
                <Typography variant="primary.b26" component="h1">
                  You appear to be offline
                </Typography>
                <StandardButton
                  type="submit"
                  variant="primary-white"
                  loading={isProvisioning}
                  fullWidth
                  onClick={handleForceReload}
                >
                  Reload
                </StandardButton>
                <Typography
                  variant="primary.l16"
                  component="h2"
                  className="NotProvisioned-supplementaryText"
                >
                  Note: You may also get this message if your account is not set
                  up properly. In this case, try logging out and in again.
                </Typography>
              </Stack>
            )
          }
          {
            /* Case account is not provisioned yet */
            justLoggedIn && !provisioningFailed && (
              <Stack>
                <Helmet>
                  <title>Setting up a new account - Moonsift</title>
                </Helmet>
                <Typography variant="primary.b26" component="h1">
                  You are about to create a NEW account with Moonsift
                </Typography>
                <Typography variant="primary.m16" component="h2">
                  If you already have an account with Moonsift, try logging in
                  with a different method.
                </Typography>
                <Stack
                  direction="row"
                  spacing={16}
                  className="NotProvisioned-buttonRow"
                >
                  <StandardButton
                    type="submit"
                    variant="primary-white"
                    fullWidth
                    onClick={handleGoBack}
                  >
                    Go back and try again
                  </StandardButton>
                  <StandardButton
                    type="submit"
                    variant="primary-white"
                    loading={isProvisioning}
                    fullWidth
                    onClick={handleProvisioning}
                  >
                    Set up new account
                  </StandardButton>
                </Stack>
              </Stack>
            )
          }
          {
            /* Case account provisioning failed */
            justLoggedIn && provisioningFailed && (
              <Stack>
                <Helmet>
                  <title>Failed to set up account - Moonsift</title>
                </Helmet>
                <Typography variant="primary.b26" component="h1">
                  Failed to set up account - please contact us at
                  hello@moonsift.com
                </Typography>
              </Stack>
            )
          }
        </div>
      </div>
    </>
  );
};

export const NotProvisioned = styled(UnstyledNotProvisioned)`
  .NotProvisioned-topMenu {
    height: 56px;
    margin-bottom: 28px;
  }

  .NotProvisioned-buttonRow {
    width: 100%;
  }

  .NotProvisioned-content {
    margin: 0 auto;
    max-width: 576px;
    min-height: 200px;
    display: flex;
    align-content: center;
    justify-content: center;
    ${({ theme }) => theme.fns.getMediaQuery({ maxWidth: 608 })} {
      margin: 0 16px;
    }
    text-align: center;
  }

  .NotProvisioned-supplementaryText {
    margin-top: 26px;
  }
`;
